/**
 * Where a session lands when no particular page was asked for.
 *
 * The v1 portal never had a home page: after login it went straight to the
 * user's default page — the `WEBUserObjects` row with `IsDefault = 1`, falling
 * back to the first page they may see (`sp_Login_GET_UserInfo`, `DefaultPage`).
 * Most accounts point at a report they open every morning, and almost all of
 * those pages are still served by the legacy app.
 *
 * Sending everyone to this app's own `/home` instead ignored that choice and
 * put a page of links between the user and their work.
 */
import { redirect } from '@tanstack/react-router';
import { fetchMenu } from '@/shared/menu/menu';
import { legacyUrl } from '@/shared/menu/href';
import type { AuthUser } from './store';

export type Landing =
  /** Client-side route of this app. */
  | { internal: true; to: string }
  /** Another application on this origin — needs a real navigation. */
  | { internal: false; href: string };

/**
 * `DefaultPage` is only an href, and the same href exists in two applications:
 * the ported pages kept their legacy paths, so `/visit-monitor` is both an
 * object of the legacy portal and a route here. Which application serves a page
 * is in the catalog (`ObjectAppID`), which the menu already carries — so the
 * menu, not a hardcoded list of paths, decides.
 *
 * When both applications claim the href, legacy wins: a page is only ever
 * reached through this app once its catalog entry says so.
 */
export async function resolveLanding(user: AuthUser | null | undefined): Promise<Landing> {
  const href = (user?.defaultPage ?? '').trim();
  if (!href) return { internal: true, to: '/home' };

  const legacy: Landing = { internal: false, href: legacyUrl(href) };

  try {
    const items = (await fetchMenu()).flatMap((g) => g.items);
    if (items.some((i) => !i.internal && i.url === legacy.href)) return legacy;
    const own = items.find((i) => i.internal && i.url === href);
    if (own) return { internal: true, to: own.url };
  } catch {
    // No menu, no way to tell the applications apart — and the odds are the
    // page is legacy.
  }

  return legacy;
}

/**
 * Send a session to its landing page from a `beforeLoad`.
 *
 * Used by every entry point that carries no page of its own: the root URL, the
 * brand logo and `/home`. `stayOn` is the route doing the asking — when the
 * landing resolves to that very route there is nothing to do, and redirecting
 * would loop.
 */
export async function enterLanding(
  user: AuthUser | null | undefined,
  stayOn?: string
): Promise<void> {
  const landing = await resolveLanding(user);

  if (landing.internal) {
    if (landing.to === stayOn) return;
    throw redirect({ to: landing.to });
  }

  // Another app on this origin: leave the SPA instead of routing. Nothing is
  // rendered in between — the browser is already navigating.
  window.location.replace(landing.href);
}
